import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	inject,
	PLATFORM_ID,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { Language, TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { MarkdownModule } from 'ngx-markdown';
import { map, Observable, switchMap } from 'rxjs';

import { CarouselComponent } from '@valk-nx/components/ui-carousel/src/lib/carousel';
import { CarouselItem } from '@valk-nx/components/ui-carousel/src/lib/carousel.interface';
import { ImageInterface } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { RatingComponent } from '@valk-nx/components/ui-rating/src/lib/rating';
import { SheetModule } from '@valk-nx/components/ui-sheet/src/lib/sheet.module';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import {
	AvailabilityWidgetComponent,
	WidgetDataActionInterface,
} from '@valk-nx/compositions/ui-availability-widget/src/lib/availability-widget.component';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import {
	fallbackLanguage,
	LANGUAGE,
	negativeHeader$,
} from '@valk-nx/core/lib/core';
import { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { AccommodationTypeModule } from '@valk-nx/core/lib/pipes/accommodation-type/accommodation-type.module';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { HotelDealData } from '@valk-nx/storyblok-services/src/lib/services/content.interface';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { TagManagerFacade } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.facade';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-hotel-by-deal-detail',
	templateUrl: './hotel-by-deal-detail.component.html',
	imports: [
		AccommodationTypeModule,
		AvailabilityWidgetComponent,
		CarouselComponent,
		CommonModule,
		MarkdownModule,
		MapComponent,
		PersuadeCardComponent,
		MiniCarouselComponent,
		MarkdownModule,
		RatingComponent,
		RouterModule,
		SheetModule,
		TabMenuComponent,
		TabMenuItemDirective,
		TranslatePipe,
	],
})
export class HotelByDealDetailComponent extends StoryblokRootDirective {
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly viewportService = inject(ViewPortService);
	private readonly tagManagerFacade = inject(TagManagerFacade);
	private readonly translate = inject(TranslateService);

	readonly cd = inject(ChangeDetectorRef);
	readonly platformId = inject(PLATFORM_ID);
	readonly bookingtool = inject(BOOKINGTOOL);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	@ViewChild('contentRef', { read: ElementRef }) contentRef: ElementRef;

	language: string = fallbackLanguage;
	dealSlug = '';
	hotelSlug = '';
	isSmallTablet$: Observable<boolean>;
	hotelDealData$: Observable<HotelDealData>;
	carouselImages: CarouselItem[] = [];
	hotelImages: ImageInterface[] = [];
	dealImages: PhotoInterface[] = [];

	maxDate: Date;
	minDate: Date;

	constructor() {
		super();
		this.language = this.rootLanguage;

		this.isSmallTablet$ = this.viewportService.isSmallTablet$;
		this.hotelDealData$ = this.sbService.translatedSlugs$.pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => {
				return (
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0]
				);
			}),
			switchMap((slug) => {
				this.dealSlug = slug.path.split('/').slice(0, -1).pop();
				this.hotelSlug = slug.path.split('/').pop();

				return this.contentService.getHotelByDeal(
					this.dealSlug,
					this.hotelSlug,
					slug.lang.toLowerCase(),
				);
			}),
			map((response) => {
				const hotelDealData = response.data;

				hotelDealData.bookingToolRedirectUrl =
					BookingRedirectHelper.getRedirectUrl(
						this.bookingtool,
						this.hotelSlug,
						'',
						hotelDealData.guid,
						this.language,
					);

				this.metadataService.updateMetaDataBatch(hotelDealData);

				this.carouselImages = hotelDealData.images.map(
					(image: { src: string; alt: string }) => {
						return {
							image: {
								src: image.src,
								alt: image.alt,
							},
							heading: hotelDealData.name,
							subHeading: this.translate.instant(
								'storyblok.pages.hotel-by-deal.carousel.deal-at-hotel',
								{
									hotelName: hotelDealData.hotel.hotel.name,
								},
							),
							link: null,
						};
					},
				);

				if (this.carouselImages.length > 0) {
					negativeHeader$.next(true);
				}

				this.maxDate =
					hotelDealData.bookable?.to &&
					dayjs(hotelDealData.bookable.to).isAfter(dayjs(), 'day')
						? dayjs(hotelDealData.bookable.to).toDate()
						: dayjs().add(1, 'year').toDate();
				this.minDate =
					hotelDealData.bookable?.from &&
					dayjs(hotelDealData.bookable.from).isAfter(dayjs(), 'day')
						? dayjs(hotelDealData.bookable.from).toDate()
						: dayjs().subtract(1, 'day').toDate();

				this.hotelImages = hotelDealData.hotel.images.map(
					(image: { src: string; alt: string }) => {
						return {
							src: image.src,
							altText: image.alt,
							width: 0,
							height: 0,
						};
					},
				);

				this.dealImages = hotelDealData.images.map(
					(image: { src: string; alt: string }) => {
						return {
							src: image.src,
							alt: image.alt,
						};
					},
				);

				return hotelDealData;
			}),
		);
	}

	widgetCTA(eventData: {
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}) {
		this.tagManagerFacade.availabilityWidget({
			...eventData,
			data: {
				...eventData.data,
				hotelSlug: this.hotelSlug,
				dealSlug: this.dealSlug,
			},
		});
	}
}
